import { useState, ComponentProps } from 'react';
import styled from 'styled-components';
import { IconSize, OpenInNewIcon } from '@ardoq/icons';
import { ViewIds } from '@ardoq/api-types';
import { dispatchAction } from '@ardoq/rxbeach';
import { urlClicked, type Size, type UrlFieldValue } from '@ardoq/graph';
import { FlexBox, Stack } from '@ardoq/layout';
import { Text } from '@ardoq/typography';
import { Link } from '@ardoq/link';
import { colors, radius } from '@ardoq/design-tokens';
import { t, type I18nLocale } from '@ardoq/i18n';
import type {
  BlocksViewLink,
  BlocksViewNode,
  GetBlocksTooltipContent,
  LinksAggregation,
} from '../types';
import { NodeTooltipComponentLabel } from './tooltips/NodeTooltipComponentLabel';
import { CanvasPortal } from './tooltips/CanvasPortal';
import { groupContentRenderer } from './tooltips/groupContentRenderer';
import { isGroup, isNode } from '../viewModel$/util';
import {
  NEW_CONDITIONAL_FORMATTING_LEAF_MULTILABEL_BACKGROUNDCOLOR,
  NEW_CONDITIONAL_FORMATTING_MULTILABEL_BORDERCOLOR,
  NEW_CONDITIONAL_FORMATTING_MULTILABEL_COLOR,
} from '@ardoq/graph';

const OtherLabel = styled.label`
  display: inline-block;
  position: relative;
  padding: 2px 8px;
  border-radius: ${radius.r4};
  border: 1px solid ${NEW_CONDITIONAL_FORMATTING_MULTILABEL_BORDERCOLOR};
  background: ${NEW_CONDITIONAL_FORMATTING_LEAF_MULTILABEL_BACKGROUNDCOLOR};
  color: ${NEW_CONDITIONAL_FORMATTING_MULTILABEL_COLOR};
  min-width: fit-content;
  overflow-wrap: anywhere;
`;

const UrlLink = styled(Link)`
  overflow-wrap: anywhere;
`;

const TooltipContainer = styled(Stack)<{ $isWide: boolean }>`
  max-width: ${({ $isWide }) => ($isWide ? 448 : 208)}px;
`;

const openUrl = (url: string) =>
  dispatchAction(urlClicked({ url, viewId: ViewIds.BLOCKS }));

const urls = (urlFieldValues: UrlFieldValue[] | null) =>
  urlFieldValues?.map(({ label, url }, index) => (
    <FlexBox key={`${label}_${index}`} gap="xsmall">
      <OpenInNewIcon
        iconSize={IconSize.MEDIUM}
        deprecated_onClick={() => openUrl(url)}
        color={colors.iconActionActive}
      />
      <UrlLink typography="text2" onClick={() => openUrl(url)} hideIcon>
        {label}
      </UrlLink>
    </FlexBox>
  ));

const multilabels = (labels: string[]) =>
  labels.map((label, index) => (
    <OtherLabel key={`${label}_${index}`}>{label}</OtherLabel>
  ));

export type NodeTooltipProps = {
  node: BlocksViewNode;
  linksAggregation: LinksAggregation;
  i18nLocale: I18nLocale;
};

const SectionHeader = styled.label`
  font-size: 12px;
  line-height: 20px;
  color: ${colors.grey500};
`;
type NodeTooltipContentProps = NodeTooltipProps & {
  canvasRef: ComponentProps<typeof CanvasPortal>['canvasRef'];
  isColumnarLayout: boolean;
};
const NodeTooltipContent = (props: NodeTooltipContentProps) => {
  const { node, linksAggregation, canvasRef, isColumnarLayout, i18nLocale } =
    props;
  const { urlFieldValues, labels } = node;

  const urlElements = urls(urlFieldValues);
  const otherLabelElements = multilabels(labels.slice(2));
  const hasUrls = Boolean(urlElements?.length);
  const hasOtherLabels = Boolean(otherLabelElements?.length);

  const groupContentLabel = t(
    i18nLocale,
    'tabview:blocks.tooltips.groupContent',
    'Group content'
  );
  const labelsLabel = t(i18nLocale, 'tabview:blocks.tooltips.labels', 'Labels');
  const attachedLinksLabel = t(
    i18nLocale,
    'tabview:blocks.tooltips.attachedLinks',
    'Attached links'
  );

  const isGroupNode = isGroup(node);
  if (isColumnarLayout && isGroupNode) {
    return (
      <FlexBox gap="small">
        <Stack gap="xsmall">
          <SectionHeader>{groupContentLabel}</SectionHeader>
          <CanvasPortal
            canvasRef={canvasRef}
            run={groupContentRenderer(node, linksAggregation, true)}
          />
        </Stack>
        <Stack gap="small">
          {hasOtherLabels && (
            <Stack gap="xsmall">
              <SectionHeader>{labelsLabel}</SectionHeader>
              <FlexBox flexWrap={true} gap="xsmall">
                {otherLabelElements}
              </FlexBox>
            </Stack>
          )}
          {hasUrls && (
            <Stack gap="xsmall">
              <SectionHeader>{attachedLinksLabel}</SectionHeader>
              <Stack gap="xsmall">{urlElements}</Stack>
            </Stack>
          )}
        </Stack>
      </FlexBox>
    );
  }
  return (
    <>
      {hasOtherLabels && (
        <Stack gap="xsmall">
          <SectionHeader>{labelsLabel}</SectionHeader>
          <FlexBox flexWrap={true} gap="xsmall">
            {otherLabelElements}
          </FlexBox>
        </Stack>
      )}
      {hasUrls && (
        <Stack gap="xsmall">
          <SectionHeader>{attachedLinksLabel}</SectionHeader>
          <Stack gap="xsmall">{urlElements}</Stack>
        </Stack>
      )}
      {isGroupNode && (
        <Stack gap="xsmall">
          <SectionHeader>{groupContentLabel}</SectionHeader>
          <CanvasPortal
            canvasRef={canvasRef}
            run={groupContentRenderer(node, linksAggregation, false)}
          />
        </Stack>
      )}
    </>
  );
};

export const NodeTooltip = (props: NodeTooltipProps) => {
  const { node } = props;
  const { urlFieldValues, labels } = node;
  const [canvasSize, setCanvasSize] = useState<Size | null>(null);
  /** the maximum width of group node tooltips is dependent on the group content. once the CanvasPortal has rendered, get the canvas size and use that to set {@link canvasSize}. */
  const updateCanvasSize = (canvas: HTMLCanvasElement | null) => {
    if (!canvas) {
      setCanvasSize(null);
      return;
    }
    const { width, height } = canvas;
    if (width !== canvasSize?.width || height !== canvasSize?.height) {
      setCanvasSize({ width, height });
    }
  };

  const isGroupNode = isGroup(node);
  const isComponentGroup = isGroupNode && node.isComponent;
  const isColumnarLayout = Boolean(
    isComponentGroup && (urlFieldValues?.length || labels.length > 2)
  );

  const isWide =
    isColumnarLayout || !canvasSize || canvasSize.width > canvasSize.height;

  return (
    <TooltipContainer gap="small" $isWide={isWide}>
      <NodeTooltipComponentLabel node={node} />
      <NodeTooltipContent
        {...props}
        canvasRef={updateCanvasSize}
        isColumnarLayout={Boolean(isColumnarLayout)}
      />
    </TooltipContainer>
  );
};

export type LinkTooltipProps = {
  link: BlocksViewLink;
  i18nLocale: I18nLocale;
};

export const LinkTooltip = ({ link, i18nLocale }: LinkTooltipProps) => {
  const urlElements = urls(link.urlFieldValues);
  const otherLabelElements = multilabels(link.labels.slice(1));
  const hasUrls = Boolean(urlElements?.length);
  const hasOtherLabels = Boolean(otherLabelElements?.length);
  const {
    labels: [name],
    source,
    target,
  } = link;

  return (
    <TooltipContainer gap="small" $isWide={true}>
      <Text variant="text2Bold">{name}</Text>
      <Stack gap="xsmall">
        <SectionHeader>
          {t(i18nLocale, 'tabview:blocks.tooltips.source', 'Source')}
        </SectionHeader>
        <NodeTooltipComponentLabel node={source} />
      </Stack>
      <Stack gap="xsmall">
        <SectionHeader>
          {t(i18nLocale, 'tabview:blocks.tooltips.target', 'Target')}
        </SectionHeader>
        <NodeTooltipComponentLabel node={target} />
      </Stack>
      {hasOtherLabels && (
        <Stack gap="xsmall">
          <SectionHeader>
            {t(i18nLocale, 'tabview:blocks.tooltips.labels', 'Labels')}
          </SectionHeader>
          <FlexBox flexWrap={true} gap="xsmall">
            {otherLabelElements}
          </FlexBox>
        </Stack>
      )}
      {hasUrls && (
        <Stack gap="xsmall">
          <SectionHeader>
            {t(
              i18nLocale,
              'tabview:blocks.tooltips.attachedLinks',
              'Attached links'
            )}
          </SectionHeader>
          <Stack gap="xsmall">{urlElements}</Stack>
        </Stack>
      )}
    </TooltipContainer>
  );
};

export const getDefaultTooltipContent: GetBlocksTooltipContent = (
  item,
  { linksAggregation, i18nLocale }
) => {
  if (isNode(item)) {
    return (
      <NodeTooltip
        node={item}
        linksAggregation={linksAggregation}
        i18nLocale={i18nLocale}
      />
    );
  }
  const hasMainLabel = item.labels[0] && item.labels[0].trim() !== '';
  const hasUrlFields = item.urlFieldValues && item.urlFieldValues.length > 0;
  const hasAdditionalLabels = item.labels.length > 1;
  if (!hasMainLabel && !hasUrlFields && !hasAdditionalLabels) {
    return null;
  }
  return <LinkTooltip link={item} i18nLocale={i18nLocale} />;
};
